import { computed, type ComputedRef } from 'vue'

import { useActiveOffer } from '@/components/hotelOffers/queries'
import { useStudioPlusCampaign } from '@/components/payment/queries'
import { useCompetitors } from '@/components/rateInsights/queries'
import { useCampaign } from '@/components/rateConnect/queries'
import { useRateConnectAuth } from '@/composables/useRateConnectAuth'
import { useSessionValues } from '@/composables/useSessionValues'
import { selectedAccommodationId, useAccommodationsByOrgId } from '@/layouts/queries'

import type { ChatContext } from '@/components/chatbot/chatContext.types'

export type { ChatContext, RateConnectContext, StudioPlusContext } from '@/components/chatbot/chatContext.types'

export interface UseChatContextReturn {
  chatContext: ComputedRef<ChatContext>
}

let cachedState: UseChatContextReturn | null = null

function initializeChatContext(): UseChatContextReturn {
  const { userId, userRole, language, selectedOrganisationId } = useSessionValues()
  const { currentAccommodation } = useAccommodationsByOrgId(selectedOrganisationId)
  const { campaignsData } = useCampaign(selectedAccommodationId)
  const { isEligible } = useRateConnectAuth()
  const { studioPlusCampaign, isExpiredSubscription, isCancelledSubscription } =
    useStudioPlusCampaign(selectedAccommodationId)
  const { hasActiveOffer } = useActiveOffer(selectedAccommodationId)
  const { hasCompetitors } = useCompetitors(selectedAccommodationId)

  const chatContext = computed(
    (): ChatContext => ({
      userId: userId.value,
      // AI service requires accommodationId to be of type string
      accommodationId: selectedAccommodationId.value ? String(selectedAccommodationId.value) : null,
      userRole: userRole.value,
      language: language.value,
      accommodationCountryCode: currentAccommodation.value?.countryCode,
      rateConnect: selectedAccommodationId.value
        ? {
            isEligible: isEligible.value,
            status: campaignsData.value?.status,
          }
        : null,
      studioPlus: selectedAccommodationId.value
        ? {
            type: studioPlusCampaign.value?.type,
            isExpiredSubscription: isExpiredSubscription.value,
            isCancelledSubscription: isCancelledSubscription.value,
            hasActiveOffer: hasActiveOffer.value,
            hasCompetitors: hasCompetitors.value,
          }
        : null,
    })
  )

  return { chatContext }
}

export function useChatContext(): UseChatContextReturn {
  if (!cachedState) {
    cachedState = initializeChatContext()
  }

  return cachedState
}
